// The end-of-timer sound, synthesized with Web Audio so there is no asset to
// ship. Browsers only allow audio after a user gesture, so primeAudio() runs on
// Start and the same context is reused when the timer rings later.

let ctx: AudioContext | null = null;

function getContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) ctx = new Ctor();
  return ctx;
}

/** Call from a click handler so the later chime is allowed to play. */
export function primeAudio(): void {
  const c = getContext();
  if (c?.state === 'suspended') void c.resume().catch(() => {});
}

/** Three soft bell notes, twice. */
export function playChime(): void {
  const c = getContext();
  if (!c) return;
  if (c.state === 'suspended') void c.resume().catch(() => {});
  const notes = [659.25, 783.99, 1046.5];
  const t0 = c.currentTime + 0.05;
  for (let round = 0; round < 2; round++) {
    notes.forEach((freq, i) => {
      const start = t0 + round * 1.1 + i * 0.16;
      const osc = c.createOscillator();
      const gain = c.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.18, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 1.4);
      osc.connect(gain).connect(c.destination);
      osc.start(start);
      osc.stop(start + 1.5);
    });
  }
}

/**
 * A short mechanical click: a detent on the dial ('minor', 'major' on every
 * fifth minute) or a key press ('press'). Quiet on purpose.
 */
export function playTick(kind: 'minor' | 'major' | 'press' = 'press'): void {
  const c = getContext();
  if (!c) return;
  if (c.state === 'suspended') void c.resume().catch(() => {});
  const t = c.currentTime;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(kind === 'press' ? 1400 : kind === 'major' ? 2300 : 1900, t);
  osc.frequency.exponentialRampToValueAtTime(kind === 'press' ? 600 : 900, t + 0.03);
  const peak = kind === 'major' ? 0.07 : kind === 'press' ? 0.05 : 0.035;
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(peak, t + 0.002);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.04);
  osc.connect(gain).connect(c.destination);
  osc.start(t);
  osc.stop(t + 0.05);
}

/** A detent you can feel on phones that support it. */
export function buzz(ms = 6): void {
  try {
    navigator.vibrate?.(ms);
  } catch {
    // Not supported: the click is enough
  }
}

/** Ask once, on Start, so a ring can reach the user in another tab. */
export function requestNotificationPermission(): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'default') return;
  void Notification.requestPermission().catch(() => {});
}

export function notify(title: string, body: string): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    const n = new Notification(title, { body, tag: 'mindfull-focus', silent: true });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {
    // Some browsers (iOS Safari) only allow notifications from a service worker.
  }
}
